import { createAsyncThunk } from '@reduxjs/toolkit';
import { Json } from '@gift/types/Json';
import { Size } from 'ff-canvas';
import { AppThunkApiConfig, WebsiteState } from '../store';
import { withAuthorization } from './authorization';
import { selectActiveVariant } from '../slices/variantsSlice';
import { selectActiveInputs } from '../slices/inputsSlice';
import { urls } from '@gift/common/_urls';
import { reportError } from '@gift/common/helpers/logging';
import { unwrap } from '@gift/common/helpers/unwrap';
import { scenePlugins } from '@gift/common/helpers/_registry';
import { selectTransformParametersForPreview } from '@gift/basic-scene-website/_redux-prefabs';

type PushProjectResult = {
    snapshotId: string
};

export const pushProject = createAsyncThunk<PushProjectResult, void, AppThunkApiConfig>(
    'auth/pushProject',
    async (_, thunkApi) => {

        const request = async (state: WebsiteState) => {
            return fetch(urls.pushProject, await formatRequest(state));
        };

        const response = await withAuthorization(request, thunkApi);

        if (response.ok) {
            return await response.json() as PushProjectResult;
        } else {
            throw new Error(`Push error: ${urls.pushProject} [${response.status}]`);
        }
    }
);

const formatRequest = async (state: WebsiteState) => {

    const formData = new FormData();
    formData.append('sceneId', state.scene.id);
    formData.append('projectJson', JSON.stringify(toProject(state)));
    formData.append('metaJson', JSON.stringify(state.meta));

    try {
        const blob = await renderThumbnail(state, { width: 300, height: 300 });
        formData.append('thumbnail', blob);
    } catch (e) {
        reportError(e);
    }

    return {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${state.project.jwt}` },
        body: formData
    };
};

const toProject = (state: WebsiteState): Json.Project<any, any, any> => ({
    version: 'variants',
    scene: state.scene,
    variant: selectActiveVariant(state),
    variables: unwrap(state.variables),
    inputs: selectActiveInputs(state),
    meta: state.meta
});

const renderThumbnail = async (
    state: WebsiteState,
    size: Size
): Promise<Blob> => {

    const project = toProject(state);
    const transformParameters = selectTransformParametersForPreview(state);

    return scenePlugins
        .require(state.scene.pluginId)
        .renderThumbnail(project, transformParameters, size);
};